import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import ManuscriptPdfPanel from "components/organisms/manuscript/ManuscriptPdfPanel";
import {
    formatReviewThreadCreatedAt,
    formatReviewThreadLocation,
    getReviewThreadExtraMessageCount,
    getReviewThreadFirstMessage,
    getReviewThreadMessageCountLabel,
    getReviewThreadSelectedText,
    getReviewThreadStatusLabel,
} from "components/organisms/manuscript/review/formatters";

const Panel = styled.section`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    height: 100%;
    overflow: hidden;
    background: #f7fafc;

    @media (max-width: 1100px) {
        display: ${(props) => (props.$collapsed ? "none" : "flex")};
        position: fixed;
        top: 40px;
        right: 40px;
        bottom: 0;
        z-index: 70;
        width: min(460px, calc(100vw - 80px));
        border-left: 1px solid #c9d6e3;
        box-shadow: -16px 0 34px rgba(15, 23, 42, 0.16);
    }
`;

const Shell = styled.div`
    display: ${(props) => (props.$collapsed ? "none" : "flex")};
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    width: 100%;
    height: 100%;
`;

const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid #dde6f0;
    background: #f6f9fc;
`;

const HeaderInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
`;

const HeaderText = styled.div`
    min-width: 0;
`;

const Title = styled.div`
    font-size: 13px;
    font-weight: 800;
    color: #132238;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Subtitle = styled.div`
    font-size: 11px;
    color: #6d7f96;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const Button = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 32px;
    padding: 0 10px;
    border: 1px solid ${(props) => (props.$active ? "#0f8f6f" : "#d8e1ec")};
    border-radius: 8px;
    background: ${(props) => (props.$primary ? "#0f8f6f" : props.$active ? "#e7faf3" : "#ffffff")};
    color: ${(props) => (props.$primary ? "#ffffff" : props.$active ? "#0f8f6f" : "#29415d")};
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${(props) => (props.$primary ? "#10765d" : props.$active ? "#def7ee" : "#eef3f7")};
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const RailBody = styled.div`
    flex: 1;
    min-height: 0;
    padding: 10px;
    overflow: auto;
    background: #fbfcfe;
`;

const Card = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px;
    border: 1px dashed #c9d6e5;
    border-radius: 12px;
    background: #ffffff;
    color: #4d6178;
`;

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const MetaList = styled.div`
    display: grid;
    grid-template-columns: minmax(88px, 110px) minmax(0, 1fr);
    gap: 8px 12px;
    font-size: 13px;
`;

const MetaKey = styled.div`
    font-weight: 700;
    color: #29415d;
`;

const MetaValue = styled.div`
    min-width: 0;
    color: #536981;
    word-break: break-word;
`;

const ReviewList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const ReviewToolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    flex-wrap: wrap;
`;

const ReviewFilterGroup = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const CompactTextButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    padding: 0 8px;
    border: 1px solid #d5dee8;
    border-radius: 6px;
    background: #ffffff;
    color: #32485e;
    font-size: 10px;
    font-weight: 700;
    cursor: pointer;

    &:hover {
        background: #eef3f7;
    }
`;

const ReviewThreadItem = styled.button`
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
    padding: 8px 10px;
    border: 1px solid ${(props) => (props.$active ? "#b8d8cc" : "#dde7f1")};
    border-radius: 9px;
    background: ${(props) => (props.$active ? "#eef9f4" : "#ffffff")};
    text-align: left;
    cursor: pointer;

    &:hover {
        background: ${(props) => (props.$active ? "#eef9f4" : "#f7fafc")};
    }
`;

const ReviewThreadTop = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const ReviewThreadTitle = styled.div`
    font-size: 11px;
    font-weight: 700;
    color: #1d3247;
`;

const ReviewThreadTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex-wrap: wrap;
`;

const ReviewThreadCount = styled.span`
    font-size: 10px;
    font-weight: 700;
    color: #6d8197;
`;

const ReviewBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 18px;
    padding: 0 7px;
    border-radius: 999px;
    background: ${(props) => (props.$resolved ? "#e5ecf4" : "#e7faf3")};
    color: ${(props) => (props.$resolved ? "#5f7489" : "#0f8f6f")};
    font-size: 9px;
    font-weight: 800;
`;

const ReviewThreadMeta = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 10px;
    color: #698096;
`;

const ReviewThreadLine = styled.div`
    font-size: 10px;
    color: #698096;
`;

const ReviewThreadSelectedText = styled.div`
    font-size: 11px;
    color: #465d74;
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
`;

const ReviewThreadMessageCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 9px 10px;
    border: 1px solid #dde7f1;
    border-radius: 10px;
    background: #fbfdff;
`;

const ReviewThreadMessageMeta = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 10px;
    color: #6d8197;
`;

const ReviewThreadPreview = styled.div`
    font-size: 11px;
    line-height: 1.4;
    color: #344b60;
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const ReviewThreadExtraMessages = styled.div`
    font-size: 10px;
    color: #6d8197;
`;

function ManuscriptRightPanel({
    activePanel,
    latestCompile,
    pdfArtifact,
    pdfRevisionSeq,
    logArtifact,
    compileRevisionSeq,
    compileLogRaw,
    compileLogEntries,
    compileLogLoading,
    compileLogLoadError,
    onCompile,
    onLogEntrySelect,
    compileLoading,
    compileDisabled,
    compileHasLintError,
    compileDisabledReason,
    historyItems,
    restoreMutation,
    handleRestoreRevision,
    saveLabel,
    reviewThreads,
    selectedReviewThreadId,
    onSelectReviewThread,
    onReviewThreadMount,
    isCollapsed,
}) {
    const [reviewFilter, setReviewFilter] = useState("open");
    const selectedReviewThreadRef = useRef(null);
    const getAuthorLabel = (name, email) => name || email || "알 수 없음";
    const visibleReviewThreads = reviewThreads.filter((thread) => {
        if (reviewFilter === "all") return true;
        if (reviewFilter === "resolved") return thread.is_resolved;
        return !thread.is_resolved;
    });
    const openReviewCount = reviewThreads.filter((thread) => !thread.is_resolved).length;
    const resolvedReviewCount = reviewThreads.filter((thread) => thread.is_resolved).length;
    const totalReviewCount = reviewThreads.length;

    useEffect(() => {
        if (!selectedReviewThreadRef.current) return;
        selectedReviewThreadRef.current.scrollIntoView({
            block: "nearest",
            behavior: "smooth",
        });
    }, [selectedReviewThreadId, reviewFilter]);

    return (
        <Panel $collapsed={isCollapsed}>
            <Shell $collapsed={isCollapsed}>
                {activePanel === "pdf" ? (
                    <ManuscriptPdfPanel
                        latestCompile={latestCompile}
                        pdfArtifact={pdfArtifact}
                        pdfRevisionSeq={pdfRevisionSeq}
                        logArtifact={logArtifact}
                        compileRevisionSeq={compileRevisionSeq}
                        compileLogRaw={compileLogRaw}
                        compileLogEntries={compileLogEntries}
                        compileLogLoading={compileLogLoading}
                        compileLogLoadError={compileLogLoadError}
                        onCompile={onCompile}
                        onLogEntrySelect={onLogEntrySelect}
                        compileLoading={compileLoading}
                        compileDisabled={compileDisabled}
                        compileHasLintError={compileHasLintError}
                        compileDisabledReason={compileDisabledReason}
                    />
                ) : null}

                {activePanel === "history" ? (
                    <>
                        <PanelHeader>
                            <HeaderInfo>
                                <Icon name={"last"} size={"16px"} color={"#0f8f6f"} />
                                <HeaderText>
                                    <Title>히스토리</Title>
                                    <Subtitle>이전 시점 복원</Subtitle>
                                </HeaderText>
                            </HeaderInfo>
                        </PanelHeader>
                        <RailBody>
                            <Stack>
                                <Card>
                                    <div>문서 변경 리비전 목록입니다.</div>
                                    <MetaList>
                                        <MetaKey>정책</MetaKey>
                                        <MetaValue>
                                            같은 작성자의 5분 이내 연속 저장은 병합. 생성, 수정, 삭제, 복원 같은 주요
                                            이벤트는 항상 별도 리비전으로 보관.
                                        </MetaValue>
                                        <MetaKey>상태</MetaKey>
                                        <MetaValue>{saveLabel}</MetaValue>
                                    </MetaList>
                                </Card>
                                {historyItems.length ? (
                                    historyItems.slice(0, 10).map((revision) => (
                                        <Card key={revision.id}>
                                            <MetaList>
                                                <MetaKey>리비전</MetaKey>
                                                <MetaValue>#{revision.revision_seq}</MetaValue>
                                                <MetaKey>유형</MetaKey>
                                                <MetaValue>{revision.revision_type}</MetaValue>
                                                <MetaKey>시간</MetaKey>
                                                <MetaValue>{revision.created_at}</MetaValue>
                                            </MetaList>
                                            <Actions>
                                                <Button
                                                    onClick={() => handleRestoreRevision(revision.id)}
                                                    disabled={restoreMutation.isLoading}
                                                >
                                                    이 시점으로 복원
                                                </Button>
                                            </Actions>
                                        </Card>
                                    ))
                                ) : (
                                    <MetaValue>아직 복원 가능한 리비전이 없습니다.</MetaValue>
                                )}
                            </Stack>
                        </RailBody>
                    </>
                ) : null}

                {activePanel === "review" ? (
                    <>
                        <PanelHeader>
                            <HeaderInfo>
                                <Icon name={"thread"} size={"16px"} color={"#0f8f6f"} />
                                <HeaderText>
                                    <Title>리뷰</Title>
                                    <Subtitle>선택 영역에 코멘트를 남깁니다.</Subtitle>
                                </HeaderText>
                            </HeaderInfo>
                        </PanelHeader>
                        <RailBody>
                            <Stack>
                                <ReviewToolbar>
                                    <ReviewFilterGroup>
                                        <CompactTextButton
                                            type="button"
                                            onClick={() => setReviewFilter("open")}
                                            style={
                                                reviewFilter === "open"
                                                    ? { borderColor: "#0f8f6f", color: "#0f8f6f" }
                                                    : undefined
                                            }
                                        >
                                            열림 ({openReviewCount})
                                        </CompactTextButton>
                                        <CompactTextButton
                                            type="button"
                                            onClick={() => setReviewFilter("resolved")}
                                            style={
                                                reviewFilter === "resolved"
                                                    ? { borderColor: "#0f8f6f", color: "#0f8f6f" }
                                                    : undefined
                                            }
                                        >
                                            해결됨 ({resolvedReviewCount})
                                        </CompactTextButton>
                                        <CompactTextButton
                                            type="button"
                                            onClick={() => setReviewFilter("all")}
                                            style={
                                                reviewFilter === "all"
                                                    ? { borderColor: "#0f8f6f", color: "#0f8f6f" }
                                                    : undefined
                                            }
                                        >
                                            전체 ({totalReviewCount})
                                        </CompactTextButton>
                                    </ReviewFilterGroup>
                                </ReviewToolbar>

                                <ReviewList>
                                    {visibleReviewThreads.length ? (
                                        visibleReviewThreads.map((thread) => {
                                            const firstMessage = getReviewThreadFirstMessage(thread);
                                            const location = formatReviewThreadLocation(thread.creation_anchor_range);
                                            const selectedText = getReviewThreadSelectedText(thread);
                                            const extraMessageCount = getReviewThreadExtraMessageCount(thread);
                                            return (
                                                <ReviewThreadItem
                                                    key={thread.id}
                                                    type="button"
                                                    data-testid={`review-thread-item-${thread.id}`}
                                                    ref={(element) => {
                                                        if (thread.id === selectedReviewThreadId) {
                                                            selectedReviewThreadRef.current = element;
                                                        }
                                                        onReviewThreadMount?.(thread.id, element);
                                                    }}
                                                    $active={thread.id === selectedReviewThreadId}
                                                    onClick={() =>
                                                        onSelectReviewThread?.(thread.id, { source: "panel" })
                                                    }
                                                >
                                                    <ReviewThreadTop>
                                                        <ReviewThreadTitleRow>
                                                            <ReviewThreadTitle>{thread.anchor_path}</ReviewThreadTitle>
                                                            <ReviewThreadCount>
                                                                {getReviewThreadMessageCountLabel(thread)}
                                                            </ReviewThreadCount>
                                                        </ReviewThreadTitleRow>
                                                        <ReviewBadge $resolved={thread.is_resolved}>
                                                            {getReviewThreadStatusLabel(thread)}
                                                        </ReviewBadge>
                                                    </ReviewThreadTop>
                                                    {location ? <ReviewThreadLine>{location}</ReviewThreadLine> : null}
                                                    {selectedText ? (
                                                        <ReviewThreadSelectedText>
                                                            {selectedText}
                                                        </ReviewThreadSelectedText>
                                                    ) : null}
                                                    <ReviewThreadMessageCard>
                                                        <ReviewThreadMessageMeta>
                                                            <span>
                                                                {getAuthorLabel(
                                                                    firstMessage?.sender_name || thread.created_by_name,
                                                                    firstMessage?.sender_email ||
                                                                        thread.created_by_email,
                                                                )}
                                                            </span>
                                                            <span>
                                                                {formatReviewThreadCreatedAt(
                                                                    firstMessage?.created_at || thread.created_at,
                                                                )}
                                                            </span>
                                                        </ReviewThreadMessageMeta>
                                                        <ReviewThreadPreview>
                                                            {firstMessage?.content || "첫 메시지가 없습니다."}
                                                        </ReviewThreadPreview>
                                                    </ReviewThreadMessageCard>
                                                    {extraMessageCount ? (
                                                        <ReviewThreadExtraMessages>
                                                            추가 메시지 {extraMessageCount}개
                                                        </ReviewThreadExtraMessages>
                                                    ) : null}
                                                </ReviewThreadItem>
                                            );
                                        })
                                    ) : (
                                        <MetaValue>표시할 리뷰가 없습니다.</MetaValue>
                                    )}
                                </ReviewList>
                            </Stack>
                        </RailBody>
                    </>
                ) : null}
            </Shell>
        </Panel>
    );
}

export default ManuscriptRightPanel;
